import {
    Avatar,
    Badge,
    Box,
    Button,
    ButtonGroup,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Popover,
    Stack,
    SvgIcon,
    Tooltip,
    Typography,
} from "@mui/material";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Bell as BellIcon } from "react-feather";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import arkadu from "src/coreConfig";
import useAsyncRequest from "src/hooks/useAsyncRequest";
import { useSelector } from "src/store";
import { resolveRelatedToPath } from "src/utils/relatedToRoutes";

const Notifications = () => {
  const { currentCustomer } = useSelector((state) => state.customers);
  const { adminMode } = useSelector((state) => state.settings);
  const { t } = useTranslation();
  const { api } = useAsyncRequest();
  const navigate = useNavigate();
  const ref = useRef(null);
  const [isOpen, setOpen] = useState(false);
  const [smartDigests, setSmartDigests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedDigest, setSelectedDigest] = useState(null);

  const notifyUrl = currentCustomer
    ? `/${currentCustomer.shortname}/notify/`
    : "/app/notify/";
  const smartNotifyUrl = currentCustomer
    ? `/${currentCustomer.shortname}/notify/smart/`
    : "/app/notify/";

  const unreadItems = useMemo(
    () => smartDigests.filter((digest) => digest.state === "unread"),
    [smartDigests],
  );

  const unreadCount = unreadItems.length;
  const previewItems = unreadItems.slice(0, 6);

  const refreshSmartNotifications = useCallback(async () => {
    if (!currentCustomer?.id) {
      return [];
    }

    const response = await api(
      arkadu.urls.v2.notify.smart.list,
      {
        customer_id: currentCustomer.id,
        mode: adminMode ? "admin" : "user",
        limit: 20,
        state: "all",
      },
      {},
      null,
      true,
    );

    if (response?.data?.res === 1) {
      const nextDigests = Array.isArray(response.data.data)
        ? response.data.data
        : [];
      setSmartDigests(nextDigests);
      return nextDigests;
    }

    return [];
  }, [api, currentCustomer?.id, adminMode]);

  const loadSmartNotifications = useCallback(async () => {
    if (!currentCustomer?.id) {
      return;
    }

    setLoading(true);
    try {
      await refreshSmartNotifications();
    } finally {
      setLoading(false);
    }
  }, [currentCustomer?.id, refreshSmartNotifications]);

  const getDigestDetail = useCallback(
    async (digestId) => {
      if (!currentCustomer?.id || !digestId) {
        return null;
      }

      const response = await api(
        arkadu.urls.v2.notify.smart.detail,
        {
          customer_id: currentCustomer.id,
          mode: adminMode ? "admin" : "user",
          id: digestId,
        },
        {},
        null,
        true,
      );

      if (response?.data?.res === 1) {
        return response.data.data;
      }

      return null;
    },
    [api, currentCustomer?.id, adminMode],
  );

  const markDigestRead = useCallback(
    async (digestId) => {
      if (!currentCustomer?.id || !digestId) {
        return null;
      }

      const response = await api(
        arkadu.urls.v2.notify.smart.markRead,
        {
          customer_id: currentCustomer.id,
          mode: adminMode ? "admin" : "user",
          id: digestId,
        },
        {},
        null,
        true,
      );

      if (response?.data?.res === 1) {
        await refreshSmartNotifications();
        return response.data.data;
      }

      return null;
    },
    [api, currentCustomer?.id, adminMode, refreshSmartNotifications],
  );

  const markAllDigestsRead = useCallback(async () => {
    if (!currentCustomer?.id) {
      return;
    }

    const response = await api(
      arkadu.urls.v2.notify.smart.markAllRead,
      {
        customer_id: currentCustomer.id,
        mode: adminMode ? "admin" : "user",
      },
      {},
      null,
      true,
    );

    if (response?.data?.res === 1) {
      setSelectedDigest(null);
      await refreshSmartNotifications();
    }
  }, [api, currentCustomer?.id, adminMode, refreshSmartNotifications]);

  const resolveDigestTarget = useCallback(
    (digest) => {
      const direct = resolveRelatedToPath({
        relatedTo: digest?.related_to,
        customerShortname: currentCustomer?.shortname,
      });
      if (direct) {
        return direct;
      }

      if (Array.isArray(digest?.items)) {
        const fromItems = digest.items
          .map((item) =>
            resolveRelatedToPath({
              relatedTo: item?.related_to,
              customerShortname: currentCustomer?.shortname,
            }),
          )
          .find(Boolean);

        if (fromItems) {
          return fromItems;
        }
      }

      return `${smartNotifyUrl}?smart_digest_id=${digest.id}`;
    },
    [currentCustomer?.shortname, smartNotifyUrl],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setSelectedDigest(null);
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
    loadSmartNotifications();
  }, [loadSmartNotifications]);

  const handleOpenDigest = useCallback(
    async (digest) => {
      if (!digest?.id) {
        navigate(smartNotifyUrl);
        handleClose();
        return;
      }

      const detail = await getDigestDetail(digest.id);
      const fullDigest = detail || digest;
      setSelectedDigest(fullDigest);
      await markDigestRead(digest.id);
    },
    [getDigestDetail, handleClose, markDigestRead, navigate, smartNotifyUrl],
  );

  const handleNavigateToDigestTarget = useCallback(
    async (digest) => {
      if (!digest?.id) {
        navigate(smartNotifyUrl);
        handleClose();
        return;
      }

      let target = resolveDigestTarget(digest);

      if (!target && currentCustomer?.id) {
        const detailResponse = await api(
          arkadu.urls.v2.notify.smart.detail,
          {
            customer_id: currentCustomer.id,
            mode: adminMode ? "admin" : "user",
            id: digest.id,
          },
          {},
          null,
          true,
        );

        if (detailResponse?.data?.res === 1) {
          target = resolveDigestTarget(detailResponse.data.data);
        }
      }

      navigate(target || `${smartNotifyUrl}?smart_digest_id=${digest.id}`);
      handleClose();
    },
    [
      smartNotifyUrl,
      resolveDigestTarget,
      currentCustomer,
      api,
      adminMode,
      handleClose,
      navigate,
    ],
  );

  useEffect(() => {
    loadSmartNotifications();
  }, [loadSmartNotifications]);

  return (
    <>
      <Tooltip title={t("Notificaciones")}>
        <IconButton color="inherit" ref={ref} onClick={handleOpen} size="large">
          <Badge color="error" badgeContent={unreadCount} max={99}>
            <SvgIcon>
              <BellIcon />
            </SvgIcon>
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        slotProps={{
          paper: {
            sx: { width: 360, maxWidth: "calc(100vw - 24px)" },
          },
        }}
      >
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="h6" color="text.primary" noWrap>
                {t("Notificaciones")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedDigest
                  ? t("Detalle del aviso")
                  : t("No le�das: {{count}}", { count: unreadCount })}
              </Typography>
            </Box>

            {selectedDigest ? (
              <Button size="small" onClick={() => setSelectedDigest(null)}>
                {t("Volver")}
              </Button>
            ) : null}
          </Stack>
        </Box>

        <Divider />

        {loading || detailLoading ? (
          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={22} />
          </Box>
        ) : selectedDigest ? (
          <Box sx={{ p: 2, width: 360, maxWidth: "calc(100vw - 24px)" }}>
            <Stack spacing={1.25}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t("T�tulo")}
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {selectedDigest.title}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t("Resumen")}
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {selectedDigest.summary || t("Sin resumen disponible")}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t("Eventos")}
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {t("{{count}} evento(s)", {
                    count: selectedDigest.items_count || 0,
                  })}
                </Typography>
              </Box>

              {Array.isArray(selectedDigest.items) &&
              selectedDigest.items.length ? (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("Detalle")}
                  </Typography>
                  <List disablePadding sx={{ mt: 0.5 }}>
                    {selectedDigest.items.map((item, index) => (
                      <ListItemButton
                        key={item.event_id || index}
                        sx={{ px: 0, py: 0.75 }}
                        onClick={() =>
                          item?.related_to
                            ? handleNavigateToDigestTarget({
                                ...selectedDigest,
                                related_to: item.related_to,
                              })
                            : null
                        }
                      >
                        <ListItemText
                          primary={
                            item.title ||
                            t("Evento {{index}}", { index: index + 1 })
                          }
                          secondary={
                            item.related_to || selectedDigest.related_to
                          }
                          primaryTypographyProps={{ variant: "body2" }}
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              ) : null}

              <ButtonGroup size="small" variant="outlined">
                <Button
                  onClick={() => handleNavigateToDigestTarget(selectedDigest)}
                  disabled={!resolveDigestTarget(selectedDigest)}
                >
                  {t("Abrir destino")}
                </Button>
                <Button
                  onClick={async () => {
                    await markDigestRead(selectedDigest.id);
                    setSelectedDigest(null);
                  }}
                >
                  {t("Marcar le�do")}
                </Button>
              </ButtonGroup>
            </Stack>
          </Box>
        ) : unreadCount === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t("No tienes notificaciones nuevas")}
            </Typography>
            <Box sx={{ pt: 1.5, display: "flex", justifyContent: "center" }}>
              <Button
                component={RouterLink}
                size="small"
                to={smartNotifyUrl}
                onClick={handleClose}
              >
                {t("Ver todas mis notificaciones")}
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{ px: 2, py: 1, display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={markAllDigestsRead}
                disabled={!unreadCount}
              >
                {t("Marcar todo le�do")}
              </Button>
              <Button
                size="small"
                onClick={loadSmartNotifications}
                disabled={loading}
              >
                {t("Recargar")}
              </Button>
            </Box>

            <List disablePadding>
              {previewItems.map((digest) => (
                <ListItemButton
                  divider
                  key={digest.id}
                  onClick={() => handleOpenDigest(digest)}
                  sx={{ py: 1, alignItems: "flex-start" }}
                >
                  <Avatar
                    sx={(theme) => ({
                      width: 30,
                      height: 30,
                      mr: 1.25,
                      mt: 0.25,
                      backgroundColor: theme.palette.info.main,
                      color: theme.palette.info.contrastText,
                    })}
                  >
                    <SvgIcon fontSize="small">
                      <BellIcon />
                    </SvgIcon>
                  </Avatar>

                  <ListItemText
                    primary={digest.title}
                    secondary={digest.summary}
                    slotProps={{
                      primary: {
                        variant: "subtitle2",
                        color: "text.primary",
                        sx: {
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        },
                      },
                      secondary: {
                        variant: "caption",
                        color: "text.secondary",
                        sx: {
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        },
                      },
                    }}
                  />
                </ListItemButton>
              ))}
            </List>

            <Box sx={{ p: 1, display: "flex", justifyContent: "center" }}>
              <Button
                component={RouterLink}
                size="small"
                to={smartNotifyUrl}
                onClick={handleClose}
              >
                {t("Ver todas mis notificaciones")}
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
};

export default Notifications;
